import React, { useState } from 'react';
import { routes } from 'constants/config';
import useFetch from 'components/shared/hooks/useFetch';
import CSRFForm from 'components/shared/CSRFForm';
import PreserveFormatting from 'components/shared/PreserveFormatting';
import InfoTooltip from 'components/shared/InfoTooltip';
import { Icon, Spinner, Card, Button, ButtonGroup } from 'ui';
import FeedbackCriteriaToggleButton from './FeedbackCriteriaToggleButton';
import AttemptPreview from '../Learner/AttemptPreview';

const useSelectedCriteria = () => {
  const [selectedCriteriaIds, setSelectedCriteria] = useState([]);
  const toggleSelectedCriteria = id => {
    let newSelectedCriteria = [];
    if (selectedCriteriaIds.includes(id)) {
      newSelectedCriteria = selectedCriteriaIds.filter(
        selectedId => selectedId !== id,
      );
    } else {
      newSelectedCriteria = [...selectedCriteriaIds, id];
    }
    setSelectedCriteria(newSelectedCriteria);
  };
  return [selectedCriteriaIds, toggleSelectedCriteria];
};

const useTextbox = () => {
  const [value, setValue] = useState('');
  const updateValue = e => {
    const text = e.target.value;
    setValue(text);
  };
  return [value, updateValue];
};

const OnDemandPracticeFeedbackGiving = ({ scenarioId, attemptId }) => {
  const { data, loading } = useFetch(
    routes.ON_DEMAND_PRACTICE.LEARNING.ATTEMPTS.GIVE_FEEDBACK(
      scenarioId,
      attemptId,
    ),
  );

  const [saving, setSaving] = useState(false);

  const [appreciativeCriteriaIds, toggleAppriciativeCriteriaIds] =
    useSelectedCriteria();
  const [criticalCriteriaIds, toggleCriticalCriteriaIds] =
    useSelectedCriteria();
  const [appreciativeComment, setAppreciativeComment] = useTextbox();
  const [criticalComment, setCriticalComment] = useTextbox();
  if (loading) {
    return (
      <div className="OnDemandPracticeFeedbackGiving">
        <div className="d-f ai-c jc-c">
          <Spinner className="mr-8" /> {t('common.loading')}
        </div>
      </div>
    );
  }
  const {
    scenario,
    attempt,
    ziggeoApiKey,
    hippoPreviewBase,
    feedbackRequest,
    feedbackCriteria,
    hasPreviouslyGivenFeedback,
  } = data;
  const hasFeedbackCriteria = feedbackCriteria.length > 0;
  const canSendFeedback =
    appreciativeComment ||
    criticalComment ||
    appreciativeCriteriaIds.length > 0 ||
    criticalCriteriaIds.length > 0;
  const disableSubmitNewFeedback =
    !canSendFeedback || hasPreviouslyGivenFeedback || saving;

  return (
    <div className="OnDemandPracticeFeedbackGiving">
      <a
        className="OnDemandPracticeFeedbackGiving-backLink"
        href={routes.ON_DEMAND_PRACTICE.LEARNING.INDEX(1)}
      >
        <Icon name="back" className="mr-4" />
        {t('practice.practice_feedback.feedback_giving.back_link')}
      </a>
      <header className="OnDemandPracticeFeedbackGiving-header">
        <h1 className="OnDemandPracticeFeedbackGiving-title">
          {t('practice.practice_feedback.feedback_giving.title')}
        </h1>
      </header>
      <div className="OnDemandPracticeFeedbackGiving-meta">
        {scenario.title} &mdash; {attempt.userName}
      </div>
      <div className="OnDemandPracticeFeedbackGiving-prompt mb-16 text-wrap">
        {scenario.prompt}
      </div>
      <Card>
        <Card.Content
          padding="large"
          className="OnDemandPracticeFeedbackGiving-attempt m-fd-c"
        >
          <div className="OnDemandPracticeFeedbackGiving-answer mr-32 m-m-0">
            <AttemptPreview
              attemptHeader={t(
                'practice.practice_feedback.feedback_giving.response_header',
                { userName: attempt.userName },
              )}
              attempt={attempt}
              ziggeoApiKey={ziggeoApiKey}
              hippoPreviewBase={hippoPreviewBase}
            />
          </div>
          <div className="OnDemandPracticeFeedbackGiving-notes">
            <strong className="d-ib mb-8">
              {t('practice.practice_feedback.feedback_giving.notes', {
                userName: attempt.userName,
              })}
            </strong>
            {feedbackRequest.note === '' && (
              <p className="fs-i c-gray-dark">
                {t('practice.practice_feedback.feedback_giving.no_notes', {
                  userName: attempt.userName,
                })}
              </p>
            )}
            <p>
              <PreserveFormatting>{feedbackRequest.note}</PreserveFormatting>
            </p>
          </div>
        </Card.Content>
      </Card>
      <div className="OnDemandPracticeFeedbackGiving-divider" />
      <CSRFForm
        method="POST"
        url={routes.ON_DEMAND_PRACTICE.LEARNING.ATTEMPTS.ATTEMPT_FEEDBACKS(
          scenario.id,
          attempt.id,
        )}
        onSubmit={() => setSaving(true)}
      >
        <Card className="mt-16">
          <Card.Content
            padding="large"
            className="OnDemandPracticeFeedbackGiving-feedback"
          >
            <header className="OnDemandPracticeFeedbackGiving-feedbackHeader">
              <h3 className="OnDemandPracticeFeedbackGiving-feedbackTitle pb-8">
                {t('practice.practice_feedback.feedback_giving.feedback_title')}
              </h3>
            </header>
            <div className="OnDemandPracticeFeedbackGiving-feedbackArea m-fd-c">
              {hasFeedbackCriteria && (
                <div className="OnDemandPracticeFeedbackGiving-feedbackAreaSide">
                  <span className="OnDemandPracticeFeedbackGiving-feedbackAreaLabel">
                    <Icon name="color_thumbs_up" className="is-32 mr-8" />
                    <strong>
                      {t(
                        'practice.practice_feedback.feedback_giving.what_went_well',
                      )}
                    </strong>
                    &nbsp;
                    <InfoTooltip
                      tooltip={t(
                        'practice.practice_feedback.feedback_giving.what_went_well_info',
                      )}
                      className="ml-4 u-hideBelowSM"
                    />
                  </span>
                  <div>
                    {feedbackCriteria.map(criterion => (
                      <FeedbackCriteriaToggleButton
                        onClick={() =>
                          toggleAppriciativeCriteriaIds(criterion.id)
                        }
                        selected={appreciativeCriteriaIds.includes(
                          criterion.id,
                        )}
                        disabled={
                          criticalCriteriaIds.includes(criterion.id) ||
                          hasPreviouslyGivenFeedback
                        }
                        criterion={criterion}
                        id={`appriciative_criterion_${criterion.id}`}
                        name="appreciativeCriteriaIds[]"
                      />
                    ))}
                  </div>
                </div>
              )}
              <div className="OnDemandPracticeFeedbackGiving-feedbackAreaSide mb-32">
                <label
                  className="OnDemandPracticeFeedbackGiving-feedbackAreaLabel fw-b"
                  htmlFor="appriciative_feedback"
                >
                  {!hasFeedbackCriteria && (
                    <span className="d-f ai-c">
                      <Icon name="color_thumbs_up" className="is-32 mr-8" />
                      <strong>
                        {t(
                          'practice.practice_feedback.feedback_giving.what_went_well',
                        )}
                      </strong>
                    </span>
                  )}
                  &nbsp;
                  {hasFeedbackCriteria &&
                    t(
                      'practice.practice_feedback.feedback_giving.specific_examples',
                    )}
                </label>
                <textarea
                  name="appreciativeComment"
                  value={appreciativeComment}
                  onChange={setAppreciativeComment}
                  className="OnDemandPracticeFeedbackGiving-input"
                  id="appriciative_feedback"
                  disabled={hasPreviouslyGivenFeedback}
                />
              </div>
            </div>
            <div className="OnDemandPracticeFeedbackGiving-feedbackArea m-fd-c">
              {hasFeedbackCriteria && (
                <div className="OnDemandPracticeFeedbackGiving-feedbackAreaSide">
                  <span className="OnDemandPracticeFeedbackGiving-feedbackAreaLabel d-f ai-c">
                    <Icon name="color_tools" className="is-32 mr-8" />
                    <strong>
                      {t(
                        'practice.practice_feedback.feedback_giving.what_improved',
                      )}
                    </strong>
                  </span>
                  <div>
                    {feedbackCriteria.map(criterion => (
                      <FeedbackCriteriaToggleButton
                        onClick={() => toggleCriticalCriteriaIds(criterion.id)}
                        selected={criticalCriteriaIds.includes(criterion.id)}
                        disabled={
                          appreciativeCriteriaIds.includes(criterion.id) ||
                          hasPreviouslyGivenFeedback
                        }
                        criterion={criterion}
                        id={`critical_criterion_${criterion.id}`}
                        name="criticalCriteriaIds[]"
                      />
                    ))}
                  </div>
                </div>
              )}
              <div className="OnDemandPracticeFeedbackGiving-feedbackAreaSide">
                <label
                  className="OnDemandPracticeFeedbackGiving-feedbackAreaLabel fw-b"
                  htmlFor="critical_feedback"
                >
                  {!hasFeedbackCriteria && (
                    <span className="d-f ai-c">
                      <Icon name="color_tools" className="is-32 mr-8" />
                      <strong>
                        {t(
                          'practice.practice_feedback.feedback_giving.what_improved',
                        )}
                      </strong>
                    </span>
                  )}
                  &nbsp;
                  {hasFeedbackCriteria &&
                    t(
                      'practice.practice_feedback.feedback_giving.specific_examples',
                    )}
                </label>
                <textarea
                  name="criticalComment"
                  value={criticalComment}
                  onChange={setCriticalComment}
                  className="OnDemandPracticeFeedbackGiving-input"
                  id="critical_feedback"
                  disabled={hasPreviouslyGivenFeedback}
                />
              </div>
            </div>
            <ButtonGroup alignEnd className="mt-16">
              <Button
                type="submit"
                customColor
                loading={saving}
                disabled={disableSubmitNewFeedback}
              >
                Send Feedback
              </Button>
            </ButtonGroup>
          </Card.Content>
        </Card>
      </CSRFForm>
    </div>
  );
};

export default props => <OnDemandPracticeFeedbackGiving {...props} />;
